/**
 * Imandra shared media / news datasource.
 *
 * Canonical, cross-product list of Imandra media: press, articles, blog posts,
 * videos, papers and white papers. This is the single source of truth consumed
 * by every Imandra web app:
 *   - imandra-web/app-www  (marketing site: /media page + home "Featured")
 *   - codelogician.dev     (home media section + /docs/media)
 *   - imandrax.dev / speclogician.dev / ipl.dev (available, opt-in)
 *
 * Superset schema: text-card fields (`description`, `source`, `type`,
 * `highlight`) and cover-card fields (`icon`, `tags`) are all
 * optional, so cover-image consumers (app-www /media) and text-card consumers
 * (codelogician) can each render the fields they need from one list.
 *
 * Ordering is never hand-maintained — render `sortByDateDesc(...)`.
 */

/** Content category used by the design-studio text cards (MediaDocCard). */
export type MediaType =
  'news' | 'article' | 'blog' | 'video' | 'paper' | 'white-paper'

/** Products a media item is relevant to (drives per-app filtering). */
export type MediaProduct =
  'imandra' | 'codelogician' | 'imandrax' | 'universe' | 'ipl' | 'speclogician'

export interface MediaItem {
  /** Headline. */
  title: string
  /** Canonical link. Internal Imandra articles are stored as absolute
   *  `https://www.imandra.ai/...` URLs; app-www normalizes them to relative. */
  url: string
  /** Human-readable date string (e.g. "22 Jun 2026", "Mar 2026", "2016").
   *  Kept human for display; use `parseMediaDate` for ordering. */
  date: string
  /** Single content category for text cards. */
  type: MediaType
  /** Products this item is relevant to. */
  products: MediaProduct[]
  /** Publisher / outlet (e.g. "YouTube", "Medium", "arXiv"). */
  source?: string
  /** Short blurb for text cards. */
  description?: string
  /** Optional emphasis pill (e.g. "+41-47pp accuracy"). */
  highlight?: string
  /** Optional brand icon key used by app-www cover cards. */
  icon?: string
  /** Feature on home "Featured" surfaces (replaces app-www `main` tag). */
  featured?: boolean
  /** Legacy app-www category tags — drive the /media page tab filter. */
  tags?: string[]
}

export const imandraMedia: MediaItem[] = [
  {
    title: 'Imandra Is Now Available on the Microsoft Marketplace',
    url: 'https://www.imandra.ai/articles/imandra-available-on-azure-marketplace',
    date: '18 Aug 2026',
    type: 'news',
    products: ['imandra', 'imandrax', 'codelogician'],
    source: 'Imandra',
    description:
      'The Imandra Automated Reasoning Framework for Capital Markets is now available on the Microsoft Marketplace — procured, deployed, and run inside your existing Azure tenant.',
    icon: 'imandra',
    featured: true,
    tags: ['news', 'main'],
  },
  {
    title:
      "BLOX Markets selects Imandra's Automated Reasoning technology to enhance Openpool Customer Onboarding and Development",
    url: 'https://www.imandra.ai/articles/blox-markets-selects-imandra-openpool',
    date: '30 Jun 2026',
    type: 'news',
    products: ['imandra', 'codelogician'],
    source: 'Imandra',
    description:
      'BLOX Markets selects Imandra Connectivity and CodeLogician to power machine-readable connectivity, certification, and formal verification for its Openpool U.S. equities trading venue.',
    icon: 'imandra',
    featured: true,
    tags: ['news', 'main'],
  },
  {
    title: 'Scaling Logic First AI Coding to Large Projects - Part 1',
    url: 'https://www.youtube.com/watch?v=4ekvFZ81Bpk',
    date: '12 Jun 2026',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    description:
      'Scaling logic-first AI coding to large projects with CodeLogician - Part 1.',
    icon: 'cl',
    tags: ['video'],
  },
  {
    title:
      'Grant Passmore Keynote: Region Decomposition - PLSE FM AI Workshop, Georgia Tech',
    url: 'https://www.linkedin.com/posts/imandra_formalmethods-neurosymbolicai-imandra-activity-7454981440318087169-MZTP?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAfFIvMBnAPJWFMFe_ctr1dRdjQa6KsxgnY',
    date: '28 Apr 2026',
    type: 'news',
    products: ['imandra', 'imandrax'],
    source: 'LinkedIn',
    description:
      "Grant Passmore's keynote on region decomposition at the PLSE Formal Methods for AI workshop at Georgia Tech.",
    icon: 'imandrax',
    featured: true,
    tags: ['news', 'main'],
  },
  {
    title: 'Codex Meets Region Decomposition with ImandraX',
    url: 'https://www.youtube.com/watch?v=ZMzcSSYHnkQ',
    date: '21 Apr 2026',
    type: 'video',
    products: ['imandra', 'imandrax'],
    source: 'YouTube',
    description:
      "Combining OpenAI Codex with ImandraX's region decomposition to reason precisely about software behavior.",
    icon: 'imandrax',
    featured: true,
    tags: ['video', 'main'],
  },
  {
    title:
      'Rigorous Edge-Case Detection for AI-Generated Code - Imandra + Gemini + Antigravity Demo',
    url: 'https://www.youtube.com/watch?v=djQzK8Ymuao',
    date: '18 Apr 2026',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    description:
      'A live demo using Imandra with Gemini and Antigravity to rigorously detect edge cases in AI-generated code.',
    icon: 'cl',
    featured: true,
    tags: ['video', 'main'],
  },
  {
    title: 'CodeLogician in the Real World: Bank Accounts & Margin Trading',
    url: 'https://www.youtube.com/watch?v=DOOcJYitKTg',
    date: '14 Apr 2026',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    icon: 'cl',
    tags: ['video'],
  },
  {
    title:
      'Get Started with CodeLogician: Install, Configure & Use Formal Verification with Claude Code',
    url: 'https://www.youtube.com/watch?v=VX0bYc_MjSo',
    date: '31 Mar 2026',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    icon: 'cl',
    tags: ['video'],
  },
  {
    title: 'Getting started with ImandraX: The Verification Workflow',
    url: 'https://www.youtube.com/watch?v=taSR1b0ipnk',
    date: '28 Mar 2026',
    type: 'video',
    products: ['imandra', 'imandrax', 'codelogician'],
    source: 'YouTube',
    description:
      'A walkthrough of the ImandraX verification workflow - from defining models to proving properties and synthesizing counterexamples with the reasoning engine.',
    icon: 'imandrax',
    tags: ['video'],
  },
  {
    title:
      'Formal AI Reasoning with ImandraX & CodeLogician | Session 1 of AI Reasoning Weekly',
    url: 'https://www.youtube.com/watch?v=nA9mEGH1lEk',
    date: '28 Mar 2026',
    type: 'video',
    products: ['imandra', 'imandrax', 'codelogician'],
    source: 'YouTube',
    description:
      'Grant Passmore and Denis Ignatovich introduce ImandraX, a theorem prover and proof assistant built for engineers, and CodeLogician, which plugs formal reasoning directly into AI coding agents like Claude Code.',
    icon: 'imandrax',
    tags: ['video'],
  },
  {
    title:
      'Imandra CodeLogician & Neurosymbolic AI: Logic-first Code Reasoning with Imandra and LLMs',
    url: 'https://www.youtube.com/watch?v=9nR5Gw9v9IE',
    date: '20 Feb 2026',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    description:
      'How CodeLogician combines statistical AI (LLMs) with symbolic AI (ImandraX) for logic-first code reasoning, verification, and test generation.',
    icon: 'cl',
    tags: ['news', 'video'],
  },
  {
    title:
      'Proving Compliance: Using Logic-Based AI Reasoning to Analyze GDPR with Imandra',
    url: 'https://www.youtube.com/watch?v=eTV9l1z5bdw',
    date: '19 Feb 2026',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    description:
      "Using Imandra's logic-based reasoning to analyze and prove compliance properties for regulations such as GDPR.",
    icon: 'cl',
    tags: ['news', 'video'],
  },
  {
    title: 'Imandra CodeLogician & Cursor: A Finance Case Study',
    url: 'https://www.youtube.com/watch?v=hL_Q4Q4GCJQ',
    date: '12 Feb 2026',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    description:
      'Denis Ignatovich and Paul Brennan walk through a real-world banking scenario of conducting bank account analysis, showcasing how AI-assisted coding via Cursor can be augmented with automated reasoning for provably correct results.',
    icon: 'cl',
    featured: true,
    tags: ['news', 'video', 'main'],
  },
  {
    title: 'M&A Term Sheet Analysis with Imandra CodeLogician and Claude',
    url: 'https://www.youtube.com/watch?v=TqHiJ05w748',
    date: '12 Feb 2026',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    description:
      "Denis Ignatovich and Paul Brennan build a reusable M&A advisory framework by feeding a real term sheet into Claude, then using Imandra's formal verification to mathematically prove whether critical safeguards hold.",
    icon: 'cl',
    featured: true,
    tags: ['video', 'main'],
  },
  {
    title:
      'Vibe Coding was phase 1. Logic-first AI is phase 2. It is here now.',
    url: 'https://medium.com/imandra/vibe-coding-was-phase-1-logic-first-ai-is-phase-2-it-is-here-now-7523beff4188',
    date: '15 Feb 2026',
    type: 'article',
    products: ['imandra', 'codelogician'],
    source: 'Medium',
    description:
      'How CodeLogician makes AI coding agents reason explicitly instead of guessing - the shift from probabilistic generation to structured, verifiable logic.',
    icon: 'cl',
    tags: ['blog'],
  },
  {
    title:
      'Managing complexity with math and logic: changing Stripe payment flow with Claude and CodeLogician',
    url: 'https://medium.com/imandra/managing-complexity-with-math-and-logic-changing-stripe-payment-flow-with-claude-and-codelogician-cdd386001e40',
    date: '07 Mar 2026',
    type: 'article',
    products: ['imandra', 'codelogician'],
    source: 'Medium',
    description:
      'How to use CodeLogician with Claude to refactor and verify a Stripe payment flow - managing complexity with formal logic and automated reasoning instead of guesswork.',
    icon: 'cl',
    tags: ['blog'],
  },
  {
    title:
      'How Formal Reasoning Extends LLM Capabilities for Software Analysis',
    url: 'https://arxiv.org/abs/2601.11840',
    date: '17 Jan 2026',
    type: 'paper',
    products: ['imandra', 'codelogician'],
    source: 'arXiv',
    description:
      'We introduce a benchmark targeting mathematical reasoning about real software logic. Across all tested models, augmenting LLMs with formal reasoning via CodeLogician closes a 41-47 percentage point accuracy gap compared to LLM-only reasoning.',
    highlight: '+41-47pp accuracy',
    icon: 'cl',
    featured: true,
    tags: ['news', 'white-papers', 'main'],
  },
  {
    title:
      "Meet Neurosymbolic AI: Amazon's Method for Enhancing Neural Networks",
    url: 'https://www.wsj.com/articles/meet-neurosymbolic-ai-amazons-method-for-enhancing-neural-networks-620dd81a',
    date: '12 Aug 2025',
    type: 'news',
    products: ['imandra', 'codelogician'],
    source: 'The Wall Street Journal',
    description:
      'The WSJ covers the rise of neurosymbolic AI and how it addresses the fundamental limitations of purely statistical LLMs in high-stakes software domains.',
    icon: 'imandrax',
    tags: ['news'],
  },
  {
    title: 'A Gentle Intro to ImandraX and CodeLogician by Denis Ignatovich',
    url: 'https://www.youtube.com/watch?v=abV-eOs2ZFs',
    date: '04 Jun 2025',
    type: 'video',
    products: ['imandra', 'imandrax', 'codelogician'],
    source: 'YouTube',
    description:
      'Denis Ignatovich (Co-Founder, Imandra) walks through the core ideas behind ImandraX and CodeLogician - how automated reasoning integrates with AI coding agents.',
    icon: 'cl',
    tags: ['news', 'video'],
  },
  {
    title:
      'Imandra Inc. Unveils Imandra Universe: The Platform for Neurosymbolic AI Agents with Logical Reasoning',
    url: 'https://www.imandra.ai/articles/imandra-universe-launch',
    date: '04 Jun 2025',
    type: 'article',
    products: ['imandra', 'universe', 'codelogician'],
    source: 'Imandra',
    description:
      'Imandra launches Imandra Universe - a unified platform bringing neurosymbolic AI and logical reasoning to software development teams at scale.',
    icon: 'universe',
    tags: ['news', 'article'],
  },
  {
    title: 'Structured Generation presented by Dr. Kostya Kanishev of Imandra',
    url: 'https://www.youtube.com/watch?v=9MzYmWAYn5M',
    date: '26 May 2025',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    description:
      'Dr. Kostya Kanishev presents on structured generation and formal reasoning at Imandra.',
    icon: 'imandra',
    tags: ['news', 'video'],
  },
  {
    title: 'Imandra CodeLogician x Cursor Demo',
    url: 'https://www.youtube.com/watch?v=6DyQPCq3rKY',
    date: '20 May 2025',
    type: 'video',
    products: ['imandra', 'codelogician'],
    source: 'YouTube',
    description:
      'A short demo of using CodeLogician with Cursor for logic-aware code analysis and verification.',
    icon: 'cl',
    tags: ['video'],
  },
  {
    title: 'First steps with ImandraX',
    url: 'https://medium.com/imandra/first-steps-with-imandrax-5c3e3a028857',
    date: '8 May 2025',
    type: 'blog',
    products: ['imandra', 'imandrax'],
    source: 'Medium',
    icon: 'imandrax',
    tags: ['blog'],
  },
  {
    title: 'Interview Series - Denis Ignatovich',
    url: 'https://www.unite.ai/denis-ignatovich-co-founder-and-co-ceo-of-imanda-interview-series/',
    date: '3 Apr 2025',
    type: 'news',
    products: ['imandra'],
    source: 'Unite.AI',
    icon: 'imandra',
    tags: ['news'],
  },
  {
    title: 'Imandra Launches CodeLogician: AI for Math Code Reasoning',
    url: 'https://aitech365.com/business-technology/it-and-devops/imandra-launches-codelogician-ai-for-math-code-reasoning/',
    date: '26 Mar 2025',
    type: 'news',
    products: ['imandra', 'codelogician'],
    source: 'AITech365',
    icon: 'cl',
    tags: ['news'],
  },
  {
    title: 'Siliconangle.com - Imandra Unveils CodeLogician',
    url: 'https://siliconangle.com/2025/03/26/imandras-new-ai-coding-assistant-codelogician-uses-reasoning-guarantee-accuracy-code/',
    date: '26 Mar 2025',
    type: 'news',
    products: ['imandra', 'codelogician'],
    source: 'SiliconANGLE',
    tags: ['news'],
  },
  {
    title: 'Yahoo.com - Imandra Unveils CodeLogician',
    url: 'https://finance.yahoo.com/news/imandra-unveils-codelogician-groundbreaking-neurosymbolic-120000921.html',
    date: '26 Mar 2025',
    type: 'news',
    products: ['imandra', 'codelogician'],
    source: 'Yahoo Finance',
    tags: ['news'],
  },
  {
    title:
      'Imandra Unveils CodeLogician: A Groundbreaking Neurosymbolic AI Agent for Mathematical Code Reasoning',
    url: 'https://www.imandra.ai/articles/imandra-releases-codelogician',
    date: '26 Mar 2025',
    type: 'news',
    products: ['imandra', 'codelogician'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'The wave breaking down AI in investing and trading.',
    url: 'https://tabbforum.com/videos/the-wave-breaking-down-ai-in-investing-and-trading/',
    date: '17 Mar 2025',
    type: 'video',
    products: ['imandra'],
    source: 'TABB Forum',
    tags: ['news', 'video'],
  },
  {
    title:
      'Imandra Inc. Advances Neurosymbolic AI Reasoning with ImandraX Release',
    url: 'https://www.imandra.ai/articles/imandra-releases-imandrax',
    date: '25 Feb 2025',
    type: 'news',
    products: ['imandra', 'imandrax'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'The start of a new AI paradigm',
    url: 'https://www.linkedin.com/pulse/start-new-ai-paradigm-ian-krietzberg-p4u8f/?trackingId=sCFI9%2B91SCydgYzyIvnLwQ%3D%3D',
    date: '10 Feb 2025',
    type: 'news',
    products: ['imandra'],
    source: 'LinkedIn',
    tags: ['news'],
  },
  {
    title: 'Observer: AI Knowledge Work in the wake of DeepSeek R1',
    url: 'https://observer.com/2025/02/openai-deep-research-deepseek-r1-comparison/',
    date: '06 Feb 2025',
    type: 'news',
    products: ['imandra'],
    source: 'Observer',
    tags: ['news'],
  },
  {
    title: 'Imandra Universe Waitlist is open for application',
    url: 'https://universe.imandra.ai',
    date: '20 Jan 2025',
    type: 'news',
    products: ['imandra', 'universe'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'Imandra Appoints Zehra Akbar as Chief Operating Officer',
    url: 'https://www.imandra.ai/articles/imandra-appoints-coo',
    date: '30 Jul 2024',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news', 'blog'],
  },
  {
    title: 'Interview with Paul Brennan at TradeTech, Paris 2024',
    url: 'https://www.youtube.com/watch?v=jDrRwgu0Qd4',
    date: '15 Jul 2024',
    type: 'video',
    products: ['imandra'],
    source: 'YouTube',
    tags: ['news', 'video'],
  },
  {
    title: 'Imandra wins the A-Team Innovation Award',
    url: 'https://www.imandra.ai/articles/imandra-wins-a-team-innovation-award',
    date: '01 May 2024',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'Imandra launches AI assistant for FIX Connectivity',
    url: 'https://www.imandra.ai/articles/imandra-fix-wizard-press-release',
    date: '22 April 2024',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'Imandra Presentation & Demo for OMG Formal Methods Working Group',
    url: 'https://www.youtube.com/watch?v=V9_cm-F3x0c',
    date: '1 March 2024',
    type: 'video',
    products: ['imandra'],
    source: 'YouTube',
    tags: ['news', 'video'],
  },
  {
    title: 'Press Release: Imandra awarded ISO 27001 certification',
    url: 'https://www.imandra.ai/articles/imandra-awarded-iso-27001-certification',
    date: '29 February 2024',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news', 'blog'],
  },
  {
    title:
      'Press Release: Imandra Gains Significant Traction across European Exchanges',
    url: 'https://www.imandra.ai/articles/imandra-gains-significant-traction',
    date: '22 February 2024',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'An Introduction to Imandra Markets',
    url: 'https://medium.com/imandra/an-introduction-to-imandra-markets-d9b7419916f2',
    date: '20 February 2024',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['news', 'blog'],
  },
  {
    title: 'AI and digital twins',
    url: 'https://tabbforum.com/opinions/ai-and-digital-twins-improving-operational-resiliency-in-financial-markets/',
    date: '15 February 2024',
    type: 'news',
    products: ['imandra'],
    source: 'TABB Forum',
    tags: ['news'],
  },
  {
    title: 'Automated Reasoning for SysML v2 Part 3',
    url: 'https://medium.com/imandra/automated-reasoning-for-sysml-v2-part-3-cfc8fc60c8af',
    date: '28 November 2023',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['news', 'blog'],
  },
  {
    title: 'Automated Reasoning for SysML v2 with Jamie Smith',
    url: 'https://www.youtube.com/watch?v=OYGyLcwnSso',
    date: '21 November 2023',
    type: 'video',
    products: ['imandra'],
    source: 'YouTube',
    tags: ['news', 'video'],
  },
  {
    title: 'Automated Reasoning for SysML v2 Part 2',
    url: 'https://medium.com/imandra/automated-reasoning-for-sysml-v2-part-2-7afd4fbd549c',
    date: '2 November 2023',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['news', 'blog'],
  },
  {
    title: 'Automated Reasoning for SysML v2 Part 1',
    url: 'https://medium.com/imandra/automated-reasoning-for-sysml-v2-ad7e87addba8',
    date: '7 August 2023',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['news', 'blog'],
  },
  {
    title: 'Imandra: automated reasoning for LLMs',
    url: 'https://www.youtube.com/watch?v=e8uFEQ3alo4&ab_channel=Imandra',
    date: '7 July 2023',
    type: 'video',
    products: ['imandra'],
    source: 'YouTube',
    tags: ['news', 'video'],
  },
  {
    title:
      'An introduction to the Imandra automated reasoning system by Grant Passmore',
    url: 'https://www.youtube.com/watch?v=PrynVs6U53M',
    date: '20 August 2019',
    type: 'video',
    products: ['imandra'],
    source: 'YouTube',
    icon: 'imandra',
    tags: ['news', 'video'],
  },
  {
    title: 'OMG approves SysML v2, beta specifications',
    url: 'http://www.omg.org/news/releases/pr2023/07-10-23.htm',
    date: '10 July 2023',
    type: 'news',
    products: ['imandra'],
    source: 'OMG',
    tags: ['news'],
  },
  {
    title: 'Interview with Imandra at TradeTech 2023',
    url: 'https://youtu.be/x2HirNwwshs',
    date: '6 July 2023',
    type: 'video',
    products: ['imandra'],
    source: 'YouTube',
    tags: ['news', 'video'],
  },
  {
    title:
      "Jamie Smith with Imandra talking about Digital Twins and insights at OMG's Quarterly Meeting.",
    url: 'https://www.youtube.com/watch?v=XazfEfEMAHk&amp;ab_channel=IndustrialTalk',
    date: '30 May 2023',
    type: 'video',
    products: ['imandra'],
    source: 'YouTube',
    tags: ['news', 'video'],
  },
  {
    title:
      "At FIXEMEA2023 we discussed how Imandra's AI can improve exchange resiliency.",
    url: 'https://youtu.be/PGUZTReIqM4',
    date: '13 Apr 2023',
    type: 'video',
    products: ['imandra'],
    source: 'YouTube',
    tags: ['news', 'video'],
  },
  {
    title:
      'Imandra is delighted to be a sponsor of the Plato Technology Summit.',
    url: 'https://www.platopartnership.com/plato-summit-2023-registration',
    date: '16 Jan 2023',
    type: 'video',
    products: ['imandra'],
    source: 'Plato Partnership',
    tags: ['video'],
  },
  {
    title:
      'Automated reasoning, a new paradigm in certification and onboarding.',
    url: 'https://www.broadridge.com/article/capital-markets/broadridges-oems-a-client-focused-trading-platform-now-and-into-the-future',
    date: '8 July 2021',
    type: 'video',
    products: ['imandra'],
    source: 'Broadridge',
    tags: ['news', 'video'],
  },
  {
    title: 'Itiviti partners with Imandra to speed FIX onboarding.',
    url: 'https://www.qa-financial.com/articles/itiviti-partners-with-imandra-to-speed-fix-onboarding ',
    date: '27 January 2021',
    type: 'video',
    products: ['imandra'],
    source: 'QA Financial',
    tags: ['news', 'video'],
  },
  {
    title:
      "World Economic Forum report on AI in the Financial Services highlights Imandra's leadership",
    url: 'https://www.weforum.org/reports/forging-new-pathways-the-next-evolution-of-innovation-in-financial-services',
    date: '15 September 2020',
    type: 'news',
    products: ['imandra'],
    source: 'World Economic Forum',
    tags: ['news'],
  },
  {
    title: 'Analysing Machine Learning Models with Imandra',
    url: 'https://medium.com/imandra/analysing-machine-learning-models-with-imandra-4510cf586927?sk=bead6ba47917a0e1a07195dcea1bb16d',
    date: '27 September 2019',
    type: 'news',
    products: ['imandra'],
    source: 'Medium',
    tags: ['news'],
  },
  {
    title: 'PyIDF: Diversity of experiences in Reinforcement Learning',
    url: 'https://medium.com/imandra/pyidf-diversity-of-experiences-in-reinforcement-learning-8d59f60f59ed',
    date: '13 September 2019',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Imandra Explains "Black-Box" Financial Algos',
    url: 'https://www.marketsmedia.com/imandra-explains-black-box-financial-algos/',
    date: '20 June 2019',
    type: 'news',
    products: ['imandra'],
    source: 'Markets Media',
    tags: ['news'],
  },
  {
    title: 'Describing Algorithms: Introduction',
    url: 'https://medium.com/imandra/describing-algorithms-introduction-8d8224a0f920',
    date: '14 June 2019',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Imandra raises $5M for tech to police other computer programs',
    url: 'https://www.bizjournals.com/austin/news/2019/04/19/imandra-gets-5m-to-police-other-tech-companys.html',
    date: '18 April 2019',
    type: 'news',
    products: ['imandra'],
    source: 'Austin Business Journal',
    tags: ['news'],
  },
  {
    title: 'Constraint solving your UIs',
    url: 'https://medium.com/imandra/constraint-solving-your-uis-8933f4cf8927',
    date: '5 February 2019',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Introducing Verified React',
    url: 'https://medium.com/imandra/introducing-verified-react-9c2ef03f821b',
    date: '7 January 2019',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Probabilistic reasoning in ReasonML',
    url: 'https://medium.com/imandra/reasoning-about-probabilities-in-reasonml-2b43bf01873e',
    date: '26 October 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Verifying ReasonReact component logic - ReasonML & Imandra',
    url: 'https://medium.com/imandra/verifying-reasonreact-component-logic-reasonml-imandra-e350d4812a9f',
    date: '4 September 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Machine Reasonable Design with Imandra',
    url: 'https://medium.com/imandra/machine-reasonable-design-with-imandra-9a7febcab6f5',
    date: '22 August 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Imandra interface to Robot OS: Part I',
    url: 'https://medium.com/imandra/imandra-interface-to-robot-os-part-i-9f3888c5c3a1',
    date: '7 august 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Scalable Deep Symbolic Reinforcement Learning with Imandra',
    url: 'https://medium.com/imandra/scalable-deep-symbolic-reinforcement-learning-with-imandra-part-i-346ebb67433a',
    date: '31 July 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Powering Documentation with Jupyter Notebooks',
    url: 'https://medium.com/imandra/powering-documentation-with-jupyter-notebooks-eb3c7ae10069',
    date: '26 July 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Creating safe robots with Imandra',
    url: 'https://medium.com/imandra/creating-safe-robots-with-imandra-a800d17fd0fe',
    date: '10 July 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Formalising the FIX Protocol in Imandra',
    url: 'https://medium.com/imandra/formalising-the-fix-protocol-in-imandra-c976ed390159',
    date: '21 June 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Analysis of the WGC Exchange using the Imandra Protocol Language',
    url: 'https://medium.com/imandra/the-wolf-goat-and-cabbage-exchange-97e7f3ff8d5a',
    date: '26 April 2018',
    type: 'blog',
    products: ['imandra', 'ipl'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title:
      'Machine Reasonable APIs and the public launch of Imandra Protocol Language',
    url: 'https://medium.com/imandra/machine-reasonable-apis-and-regulations-20f29e1bd4cf',
    date: '5 April 2018',
    type: 'blog',
    products: ['imandra', 'ipl'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title: 'Industry Looks to Formalize FIX Ecosystem',
    url: 'http://marketsmedia.com/industry-formalize-fix-ecosystem/',
    date: '24 April 2018',
    type: 'news',
    products: ['imandra'],
    source: 'Markets Media',
    tags: ['news'],
  },
  {
    title: 'The IEX Auction: Pursuing Greater Clarity',
    url: 'https://medium.com/imandra/the-iex-auction-pursuing-greater-clarity-481172789d64',
    date: '11 January 2018',
    type: 'blog',
    products: ['imandra'],
    source: 'Medium',
    tags: ['blog'],
  },
  {
    title:
      'Release of Imandra EVM: A formal model of the Ethereum Virtual Machine',
    url: 'https://www.imandra.ai/articles/release-imandra-evm-formal-model-ethereum-virtual-machine',
    date: '31 October 2016',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'Formal Verification Platform for Blockchain-Based Smart Contracts',
    url: 'https://www.cryptocoinsnews.com/aesthetic-integration-launches-smart-contract-formal-verification-platform/',
    date: '27 September 2016',
    type: 'news',
    products: ['imandra'],
    source: 'CryptoCoinsNews',
    tags: ['news'],
  },
  {
    title: "Google's Schindler Invests in UK Automated Reasoning Startup",
    url: 'https://www.cryptocoinsnews.com/aesthetic-integration-launches-smart-contract-formal-verification-platform/',
    date: '13 August 2016',
    type: 'news',
    products: ['imandra'],
    source: 'Markets Media',
    tags: ['news'],
  },
  {
    title: 'AI Submits Reg AT Comment Letter',
    url: 'https://www.imandra.ai/articles/ai-submits-reg-at-comment-letter',
    date: '16 March 2016',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: "Intel's Pentium Bug Fix Is Proposed as Solution for Dark Pools",
    url: 'http://www.bloomberg.com/news/articles/2016-03-04/intel-s-pentium-bug-fix-is-proposed-as-solution-for-dark-pools',
    date: '4 March 2016',
    type: 'news',
    products: ['imandra'],
    source: 'Bloomberg',
    tags: ['news'],
  },
  {
    title: 'AI Submits Reg ATS-N Comment Letter',
    url: 'https://www.imandra.ai/articles/ai-submits-reg-ats-n-comment-letter',
    date: '1 March 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'Financial Industry Looks to Automated Reasoning',
    url: 'http://marketsmedia.com/financial-industry-looks-to-automated-reasoning/',
    date: '17 December 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Markets Media',
    tags: ['news'],
  },
  {
    title: 'UBS Future of Finance Challenge: Monocle 24 Radio',
    url: 'http://monocle.com/radio/shows/the-bulletin-with-ubs/62/',
    date: '13 December 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Monocle',
    tags: ['news'],
  },
  {
    title: 'UBS crowns winners in Future of Finance competition',
    url: 'https://www.finextra.com/news/announcement.aspx?pressreleaseid=62449',
    date: '11 December 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Finextra',
    tags: ['news'],
  },
  {
    title: 'AI wins UBS Future of Finance Challenge',
    url: 'https://www.imandra.ai/articles/ai-wins-ubs-future-of-finance-challenge',
    date: '11 December 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news', 'blog'],
  },
  {
    title: 'AI Submits IEX Comment Letter',
    url: 'https://www.imandra.ai/articles/ai-submits-iex-comment-letter',
    date: '18 November 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'AI is a Regional Finalist of The UBS Future of Finance Challenge',
    url: 'https://www.imandra.ai/articles/ai-is-a-regional-finalist-of-the-ubs-future-of-finance-challenge',
    date: '25 October 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'UBS Future Of Finance Regional Finals',
    url: 'https://www.imandra.ai/articles/ubs-future-of-finance-regional-finals',
    date: '17 October 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['news'],
  },
  {
    title: 'Case Study: 2015 SEC Fine Against UBS ATS',
    url: 'https://www.imandra.ai/articles/case-study-2015-sec-fine-against-ubs-ats',
    date: '12 October 2015',
    type: 'white-paper',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['white-papers'],
  },
  {
    title: 'Market stress puts focus on high-speed technology',
    url: 'http://on.ft.com/1MapX9q',
    date: '6 October 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Financial Times',
    tags: ['news'],
  },
  {
    title: 'Dark pools back in the dock as regulators scrutinise ITG',
    url: 'http://on.ft.com/1L4QClw',
    date: '12 October 2015',
    type: 'news',
    products: ['imandra'],
    source: 'Financial Times',
    tags: ['news'],
  },
  {
    title: 'Transparent Order Priority and Pricing',
    url: 'https://www.imandra.ai/articles/transparent-order-priority-and-pricing',
    date: '8 August 2015',
    type: 'white-paper',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['white-papers'],
  },
  {
    title: 'Creating Safe and Fair Markets',
    url: 'https://www.imandra.ai/articles/creating-safe-and-fair-markets',
    date: '2 April 2015',
    type: 'white-paper',
    products: ['imandra'],
    source: 'Imandra',
    tags: ['white-papers'],
  },
  {
    title: 'The year the Flash Boys came into the firing line',
    url: 'https://www.fnlondon.com/amp/articles/year-end-review-2014-trading-technology-flash-boys-michael-lewis-20141217',
    date: '17 December 2014',
    type: 'news',
    products: ['imandra'],
    source: 'Financial News',
    tags: ['news'],
  },
  {
    title: 'Rethinking exchange reliability',
    url: 'http://www.ft.com/cms/s/0/aa00f35e-6409-11e4-bac8-00144feabdc0.html',
    date: '14 November 2014',
    type: 'news',
    products: ['imandra'],
    source: 'Financial Times',
    tags: ['news'],
  },
]

const MONTHS: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
}

/**
 * Parse the human `date` string into a sortable key.
 *
 * Handles every format present in the data: "22 Jun 2026", "14 November 2014",
 * "7 august 2018" (lowercase month), "Mar 2026" (month + year), and a bare
 * "2016". Items missing a day are treated as day 0 so that, within the same
 * month, dated items sort ahead of month-only items (deterministic tie-rule).
 */
export function parseMediaDate(dateStr: string): number {
  const tokens = dateStr.trim().split(/\s+/)
  let year = 0
  let month = 0
  let day = 0
  for (const token of tokens) {
    const lower = token.toLowerCase()
    if (/^\d{4}$/.test(token)) {
      year = parseInt(token, 10)
    } else if (lower in MONTHS) {
      month = MONTHS[lower]!
    } else if (/^\d{1,2}$/.test(token)) {
      day = parseInt(token, 10)
    }
  }
  return year * 10000 + month * 100 + day
}

/** Returns a new array sorted newest-first by `date`. */
export function sortByDateDesc(items: MediaItem[]): MediaItem[] {
  return [...items].sort(
    (a, b) => parseMediaDate(b.date) - parseMediaDate(a.date),
  )
}

export interface MediaFilter {
  /** Keep items relevant to ANY of these products. */
  products?: MediaProduct[]
  /** Keep items whose `type` is one of these. */
  types?: MediaType[]
  /** When true, keep only `featured` items. */
  featured?: boolean
  /** Keep items whose legacy `tags` include this value (app-www tabs). */
  tag?: string
}

/** Filter the canonical list (does not sort — compose with `sortByDateDesc`). */
export function filterMedia(
  filter: MediaFilter = {},
  items: MediaItem[] = imandraMedia,
): MediaItem[] {
  const { products, types, featured, tag } = filter
  return items.filter((item) => {
    if (featured && !item.featured) return false
    if (products && !products.some((p) => item.products.includes(p)))
      return false
    if (types && !types.includes(item.type)) return false
    if (tag && tag !== 'all' && !(item.tags ?? []).includes(tag)) return false
    return true
  })
}

/** Distinct content types present in `items`, in canonical display order. */
export function mediaTypesPresent(items: MediaItem[]): MediaType[] {
  const order: MediaType[] = [
    'news',
    'article',
    'blog',
    'video',
    'paper',
    'white-paper',
  ]
  const present = new Set(items.map((i) => i.type))
  return order.filter((t) => present.has(t))
}
